import type { PhieuNhapDieuChuyenChiTiet, PhieuNhapDieuChuyenFormData } from '@/types/schemas';
import { isValidUUID } from '@/lib/uuid-validator';
import { FormFieldState } from '../hooks';

const toNumber = (value: unknown) => Number(value || 0);

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: PhieuNhapDieuChuyenChiTiet[]) => {
  return detailRows.map((row, index: number) => ({
    line: index + 1,
    uuid: row.uuid && isValidUUID(row.uuid) ? row.uuid : null,
    ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
    dvt: row.ma_vt_data?.dvt || row.dvt || '',
    ma_kho: row.ma_kho_data?.uuid || row.ma_kho || '',
    ma_khon: row.ma_khon_data?.uuid || row.ma_khon || '',
    ma_vi_tri: row.ma_vi_tri_data?.uuid || row.ma_vi_tri || '',
    ma_vi_trin: row.ma_vi_trin_data?.uuid || row.ma_vi_trin || '',
    ma_lo: row.ma_lo || '',
    sl_ton: row.sl_ton || 0,
    so_luong: row.so_luong || 0,
    px_dd: row.px_dd || 0,
    gia_nt: row.gia_nt || 0,
    tien_nt: toNumber(row.gia_nt) * toNumber(row.so_luong) || toNumber(row.tien_nt),
    gia: row.gia || 0,
    tien: row.tien || 0,
    tk_vt: row.tk_vt_data?.uuid || row.tk_vt || '',
    ma_nx: row.ma_nx_data?.uuid || row.ma_nx || '',
    tk_du: row.tk_du_data?.uuid || row.tk_du || '',
    ghi_chu: row.ghi_chu || '',
    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || '',
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || '',
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || '',
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || '',
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || '',
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || '',
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || '',
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || '',
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || '',
    so_ct_pn: row.so_ct_pn || ''
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param entityUnit - Entity unit information
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: PhieuNhapDieuChuyenFormData,
  state: FormFieldState,
  detailRows: PhieuNhapDieuChuyenChiTiet[] = [],
  entityUnit: { uuid?: string } | null | undefined
) => {
  const chi_tiet = transformDetailRows(detailRows);

  return {
    ...data,
    unit_id: entityUnit?.uuid || '',
    ma_ngv: '3',
    ma_nk: state.selectedDocument?.uuid || '',
    so_ct: state.selectedDocumentNumber || '',
    i_so_ct: state.selectedDocument?.i_so_ct_ht || '',
    ma_kho: state.selectedWarehouse?.uuid || '',
    ma_kh_vc: state.selectedCustomer?.uuid || '',
    ten_kh_vc: data.ten_kh_vc || state.selectedCustomer?.customer_name || '',
    ma_ptvc: state.selectedVehicle?.uuid || '',
    ngay_ct: data.ngay_ct || '',
    ngay_lct: data.ngay_ct || data.ngay_lct || '',
    ma_nt: data.ma_nt || 'VND',
    ty_gia: data.ty_gia || 1,
    status: data.status || '0',
    ma_tthddt: data.ma_tthddt || '0',
    so_ct_hddt: data.so_ct_hddt || '',
    ma_pttt: data.ma_pttt || '',
    ngay_ct_hddt: data.ngay_ct_hddt || '',
    lenh_dd: data.lenh_dd || '',
    so_ct2_hddt: data.so_ct2_hddt || '',
    ngay_dd: data.ngay_dd || '',
    ma_mau_ct_hddt: data.ma_mau_ct_hddt || '',
    nguoi_dd: data.nguoi_dd || '',
    viec_dd: data.viec_dd || '',
    so_hd_vc: data.so_hd_vc || '',
    ly_do_huy: data.ly_do_huy || '',
    ly_do: data.ly_do || '',
    t_so_luong: chi_tiet.reduce((acc: number, row) => acc + toNumber(row.so_luong), 0),
    t_tien_nt: chi_tiet.reduce((acc: number, row) => acc + toNumber(row.tien_nt), 0),
    t_tien: chi_tiet.reduce((acc: number, row) => acc + toNumber(row.tien), 0),
    dien_giai: data.dien_giai || '',

    chi_tiet
  };
};
